/**
 * Date helpers for the planner and friends.
 *
 * Model B (locked): calendar boundaries follow the **customer's wall clock**.
 * `startOfToday()` derives "today" from the browser timezone (`Intl`), and
 * helpers that operate on `YYYY-MM-DD` strings round-trip through UTC-encoded
 * `Date`s for arithmetic (no Temporal polyfill here: this module sits in the
 * storefront wizard-embed bundle, and the polyfill alone is ~36 KB gzipped —
 * `useMyTrainingScroll` still pulls it for the SPA). The `Date` objects returned here carry
 * the customer's calendar y/m/d as **UTC** fields (so a downstream
 * `getUTCMonth()` reads the calendar month, not whatever zone the JS engine
 * is in) — that's an implementation convention shared with the server's
 * `@db.Date` round-trip. Display formatters that take a `Date` pin
 * `timeZone: "UTC"` to read those UTC fields back as the calendar value the
 * caller intended.
 *
 * If you find yourself reaching for `getMonth()` (local) here, you almost
 * certainly want `getUTCMonth()` — see the test suite for boundary cases.
 */

export const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
export const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
] as const;

/** Browser timezone (IANA). Evaluated each call so a tab moved across DST picks up the change. */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/** `YYYY-MM-DD` → Date whose UTC y/m/d are the calendar day (no time-of-day). */
export function ymdToDate(s: string): Date {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

/** "Apr 19" from a UTC calendar date. */
export function formatDateShort(d: Date): string {
  return `${MONTH_NAMES[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

/**
 * Compact range label for plan windows — "Jun 2 – 8" within a month,
 * "Jun 2 – Jul 1" when it spans months.
 */
export function formatDateRangeShort(start: Date, end: Date): string {
  if (start.getUTCMonth() === end.getUTCMonth()) {
    return `${formatDateShort(start)} – ${end.getUTCDate()}`;
  }
  return `${formatDateShort(start)} – ${formatDateShort(end)}`;
}

/** "Apr 19" from an ISO datetime — UTC calendar month and day. */
export function formatIsoDateShort(iso: string | null | undefined): string | null {
  if (iso == null || iso === "") return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${MONTH_NAMES[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

/**
 * "Apr 19, 2026" from an ISO datetime — UTC calendar date with year, no weekday.
 * Model B: format off the UTC calendar day so the displayed date never shifts
 * with the renderer's local timezone (replaces raw `toLocaleDateString()`).
 */
export function formatIsoDateLong(iso: string | null | undefined): string | null {
  if (iso == null || iso === "") return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${MONTH_NAMES[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

/**
 * "Apr 19" from an ISO INSTANT — the viewer's LOCAL calendar day.
 *
 * The counterpart to {@link formatIsoDateShort}: that one is for Model B
 * calendar dates, whose y/m/d are carried in UTC and must never shift with the
 * renderer's zone. A true instant (a TTL deadline, a completion time) is the
 * opposite case — slicing its UTC day tells a customer in Los Angeles their
 * plan expired tomorrow.
 */
export function formatInstantDateShort(iso: string | null | undefined): string | null {
  if (iso == null || iso === "") return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * "April 19, 2026" from an ISO INSTANT — the viewer's LOCAL calendar day. The
 * long-form sibling of {@link formatInstantDateShort}; `formatIsoDateLong` is
 * for Model B calendar dates and pins to UTC on purpose.
 */
export function formatInstantDateLong(iso: string | null | undefined): string | null {
  if (iso == null || iso === "") return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${MONTH_NAMES[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/** `YYYY-MM-DD` from a Date whose UTC y/m/d represent the calendar day. */
export function dateToYmd(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Returns the Monday of the week containing `d`. */
export function mondayOf(d: Date): Date {
  const dayOfWeek = d.getUTCDay(); // 0=Sun..6=Sat
  const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const r = new Date(d.getTime());
  r.setUTCDate(d.getUTCDate() + diff);
  r.setUTCHours(0, 0, 0, 0);
  return r;
}

export function addDays(d: Date, n: number): Date {
  const r = new Date(d.getTime());
  r.setUTCDate(d.getUTCDate() + n);
  return r;
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getUTCFullYear() === b.getUTCFullYear() &&
    a.getUTCMonth() === b.getUTCMonth() &&
    a.getUTCDate() === b.getUTCDate()
  );
}

export function formatDur(min: number): string {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (m === 0) return `${h}h`;
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

/** `Mon-Sun` short-day label index from a UTC date. Mon=0..Sun=6. */
export function dayIndexMonStart(d: Date): number {
  return (d.getUTCDay() + 6) % 7;
}

/**
 * Map a Mon-relative session day (0=Mon..6=Sun) in plan week `weekIndex` to a
 * calendar date anchored at `planStart` (the fuel window's first active day).
 * Sessions before `planStart`'s weekday roll into the following plan week — a
 * Wed-start plan places Wed–Sun in week 0 and Mon–Tue in week 1.
 */
export function planWeekSessionDate(planStart: Date, sessionDay: number, weekIndex: number): Date {
  const planStartDow = dayIndexMonStart(planStart);
  const offset =
    sessionDay >= planStartDow ? sessionDay - planStartDow : 7 - planStartDow + sessionDay;
  return addDays(planStart, offset + weekIndex * 7);
}

/**
 * Calendar "today" in the browser's timezone, returned as a Date with the
 * calendar y/m/d encoded into UTC fields (so `getUTCDate()` reads back the
 * customer-local calendar day). The server agrees on the same definition by
 * reading `X-Client-Timezone`.
 */
export function startOfToday(zone: string = browserTimezone()): Date {
  return ymdToDate(localTodayYmd(zone));
}

/** `YYYY-MM-DD` for "today" in the given timezone (defaults to the browser's). */
export function localTodayYmd(zone: string = browserTimezone(), now: Date = new Date()): string {
  // Assembled from `formatToParts` (not `format`) so the result never depends
  // on a locale's punctuation; `en-CA` is only chosen for its numeric parts.
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? "";
  return `${part("year").padStart(4, "0")}-${part("month")}-${part("day")}`;
}

/** Shift a YMD string by `days` calendar days. */
export function addDaysYmd(ymd: string, days: number): string {
  return dateToYmd(addDays(ymdToDate(ymd), days));
}

/** Lexicographic YMD comparison — safe because `YYYY-MM-DD` sorts correctly. */
export function compareYmd(a: string, b: string): -1 | 0 | 1 {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * Human-friendly label for a planner `YYYY-MM-DD` string. The formatter pins
 * `timeZone: "UTC"` because the value passed in is already a calendar YMD —
 * we don't want `Intl` to shift it by the renderer's local offset.
 */
export function formatYmdLong(ymd: string, locales?: Intl.LocalesArgument): string {
  const parts = ymd.trim().split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return "";
  const [y, m, d] = parts;
  const date = new Date(Date.UTC(y!, m! - 1, d!));
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locales ?? undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC"
  }).format(date);
}

/** `YYYY-MM-DD` calendar string → `MM/DD/YYYY` for US-style picker labels. */
export function formatYmdUsSlash(ymd: string): string {
  const trimmed = ymd.trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (!m) return "";
  const [, y, mo, d] = m;
  return `${mo}/${d}/${y}`;
}

/**
 * Build the array of weeks (Mon-Sun rows) that cover `N` weeks forward from
 * today. Each row contains 7 entries; the first row starts at the Monday of
 * today's week so today is always visible.
 */
export function weekRowsFromToday(N: number, today: Date = startOfToday()): Date[][] {
  const startMonday = mondayOf(today);
  const todayOffset = dayIndexMonStart(today);
  const weekCount = Math.floor((todayOffset + 7 * N - 1) / 7) + 1;
  const out: Date[][] = [];
  for (let w = 0; w < weekCount; w++) {
    const wkStart = addDays(startMonday, w * 7);
    const days: Date[] = [];
    for (let d = 0; d < 7; d++) days.push(addDays(wkStart, d));
    out.push(days);
  }
  return out;
}

export function isInRange(d: Date, from: Date, to: Date): boolean {
  return d.getTime() >= from.getTime() && d.getTime() <= to.getTime();
}

/**
 * Display an imported-activity instant in its own (provider-reported) zone with
 * a DST-correct short label — "PDT", "EST", "CET", etc. Falls back to UTC
 * offset notation only when `zone` is null or unrecognised by Intl. Use this
 * for the time + TZ suffix on imported-activity rows in Activities History.
 *
 * Example: `formatActivityTimeWithZone("2026-05-22T18:00:00Z", "America/Los_Angeles")`
 * → `"11:00 AM PDT"`.
 */
export function formatActivityTimeWithZone(
  iso: string,
  zone: string | null,
  locale: Intl.LocalesArgument = "en-US"
): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  try {
    const fmt = new Intl.DateTimeFormat(locale, {
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
      timeZone: zone ?? undefined
    });
    return fmt.format(d);
  } catch {
    return new Intl.DateTimeFormat(locale, {
      hour: "numeric",
      minute: "2-digit"
    }).format(d);
  }
}
